import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { fieldWrapper, fieldWrapperInput } from '../ui/field-surface';
import { Popover, PopoverTrigger } from '../ui/popover';
import { cn } from '../../lib/utils';
import { inFormCurrency, quotedNote, type QuotedPrice } from './catalog-suggestions';
import { CatalogSourcePicker, type CatalogOption } from './CatalogSourcePicker';
import { panelCount, panelMoney, panelPercent } from './panel-format';
import { applyPriceEdit, tierEconomics, type TierEntryMode } from './tier-pricing';

export type PriceRowValues = {
	cost: string;
	salePrice: string;
	markup: string;
	entryMode: TierEntryMode;
	minDays: string;
	maxDays: string;
	/** The catalogue price these figures came from — quantity and supplier
	 *  together, since a quantity alone can name two rows. */
	sourceQuantity?: number | null;
	sourceVetterEntityId?: string | null;
	/** Set once the admin acts. An untouched tier is the one a suggestion offers to fill. */
	touched?: boolean;
	/** True only while the exact-match rule owns this row; any edit clears it. */
	autoApplied?: boolean;
	/** What was really quoted when the cost was converted into this form's currency. */
	quoted?: QuotedPrice | null;
};

/**
 * One quantity tier: what it costs, what it sells for, and what that earns.
 *
 * A card rather than a table row. The figures an admin sets sit on one line as
 * labelled fields, and the consequence — line total, margin, profit — sits
 * opposite the quantity at the top, so the decision and its result are read
 * together without either being a column header away.
 *
 * Where the numbers came from is stated on the same line: a catalogue break, or
 * typed by hand. An untouched tier shows its suggested figures as placeholders,
 * so the catalogue is visible in the fields it would fill without having
 * filled them.
 */
export function PriceRowCard({
	quantity,
	values,
	currency,
	options,
	onChange,
	onPick,
	onRemove,
	extra,
	assignable,
	authoredCurrency,
}: {
	quantity: number;
	values: PriceRowValues;
	currency: string;
	/** Every catalogue price available for this tier. Empty when there are none. */
	options: CatalogOption[];
	onChange: (next: PriceRowValues) => void;
	onPick: (option: CatalogOption) => void;
	onRemove?: () => void;
	/** Feasibility, or anything else this tier needs beneath its fields. */
	extra?: React.ReactNode;
	/** Passed to the picker: may picking another supplier's price reassign? */
	assignable?: boolean;
	/** What this tier was originally priced in, when that was recorded. */
	authoredCurrency?: string | null;
}) {
	const [pickerOpen, setPickerOpen] = useState(false);

	const matched = options.find((o) => o.matched);
	/* Until the admin acts, the matched break is a SUGGESTION — offered by the
	   pill and its Use button, never written into the fields, because the
	   catalogue fills a form and never sets a price. */
	const suggestion = !values.touched ? matched : undefined;
	const picked = values.sourceQuantity
		? options.find(
				(o) =>
					o.quantity === values.sourceQuantity &&
					(o.vetterEntityId ?? null) === (values.sourceVetterEntityId ?? null)
			)
		: undefined;

	const economics = tierEconomics(values.cost, values.salePrice, quantity);
	/*
	 * No ghost figures in the fields.
	 *
	 * They were the design's way of showing what the catalogue would fill, but a
	 * number inside an input reads as a number the input holds, however faintly
	 * it is drawn — and these fields are empty until Use is pressed. The
	 * suggestion stays visible where it cannot be mistaken for a value: the pill
	 * names the break, and the picker lists every figure.
	 */

	/*
	 * Typing hands the row to the admin.
	 *
	 * `autoApplied` is what lets the exact-match rule re-resolve a row when the
	 * supplier changes under it. Leaving it set after a manual edit meant that
	 * rule could overwrite figures somebody typed — so an edit clears it.
	 */
	const set = (field: 'cost' | 'salePrice' | 'markup', value: string) =>
		onChange({
			...values,
			...applyPriceEdit(values, field, value),
			touched: true,
			autoApplied: false,
			// A cost typed over a converted one is the admin's own figure.
			quoted: field === 'cost' ? null : values.quoted,
		});
	const suggestionUsable = !!suggestion && !!inFormCurrency(suggestion, currency);

	return (
		<div className="flex flex-col rounded-[var(--r-3)] border border-line-2 bg-surface">
			<div className="flex items-center gap-2 border-b border-line-1 px-2.5 py-1.5">
				<span className="min-w-12 font-mono text-sm font-medium text-text-1">
					{panelCount(quantity)}
					<small className="ml-1 font-sans text-2xs font-normal text-text-3">pcs</small>
				</span>

				{options.length > 0 && (
					<Popover open={pickerOpen} onOpenChange={setPickerOpen}>
						<div className="flex items-center gap-1.5">
							<PopoverTrigger
								className={cn(
									'flex items-center gap-1 rounded-full border px-2 py-0.5 text-2xs transition-colors',
									suggestion
										? 'border-accent-line bg-accent-soft text-text-1'
										: 'border-line-2 bg-surface-fill text-text-2 hover:border-line-3 hover:text-text-1'
								)}
							>
								<span>
									{suggestion
										? 'Suggested'
										: picked
											? 'Catalogue'
											: 'Manual price'}
								</span>
								{(suggestion || picked) && (
									<span className="font-mono">
										{panelCount((suggestion ?? picked)!.quantity)}
									</span>
								)}
								<ChevronDown size={11} className="text-text-3" />
							</PopoverTrigger>
							{suggestion && (
								<button
									type="button"
									disabled={!suggestionUsable}
									title={
										!suggestionUsable
											? `Priced in ${suggestion.currency}, with no ${currency} figure for it.`
											: undefined
									}
									onClick={() => onPick(suggestion)}
									className="rounded-[var(--r-2)] border border-accent bg-accent px-2 py-0.5 text-2xs text-accent-fg transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
								>
									Use
								</button>
							)}
						</div>
						<CatalogSourcePicker
							quantity={quantity}
							assignable={assignable}
							formCurrency={currency}
							options={options}
							selectedQuantity={values.sourceQuantity}
							selectedVetterEntityId={values.sourceVetterEntityId}
							onPick={(o) => {
								onPick(o);
								setPickerOpen(false);
							}}
							onManual={() => {
								onChange({
									...values,
									sourceQuantity: null,
									sourceVetterEntityId: null,
									quoted: null,
									touched: true,
								});
								setPickerOpen(false);
							}}
						/>
					</Popover>
				)}

				<div className="ml-auto flex flex-col items-end gap-px text-right">
					<span className="font-mono text-sm font-medium text-text-1">
						{economics ? panelMoney(economics.lineTotal, currency) : '—'}
					</span>
					<span className="text-2xs text-text-3">
						{economics?.marginPercentage != null
							? `${panelPercent(economics.marginPercentage)} margin · ${panelMoney(
									economics.profitTotal,
									currency
								)} profit`
							: 'Line total'}
					</span>
				</div>

				{onRemove && (
					<button
						type="button"
						onClick={onRemove}
						title="Remove this quantity"
						className="h-6 w-6 rounded-[var(--r-2)] text-text-4 transition-colors hover:bg-surface-hover hover:text-destructive"
					>
						×
					</button>
				)}
			</div>

			<div className="grid grid-cols-[repeat(3,minmax(0,1fr))_minmax(0,1.1fr)] gap-2 px-2.5 pb-2.5 pt-2">
				<PriceField
					label="Unit cost"
					unit={currency}
					value={values.cost}
					onChange={(v) => set('cost', v)}
				/>
				<PriceField
					label="Markup"
					unit="%"
					value={values.markup}
					onChange={(v) => set('markup', v)}
				/>
				<PriceField
					label="Sale price / unit"
					unit={currency}
					value={values.salePrice}
					onChange={(v) => set('salePrice', v)}
				/>
				<div className="flex min-w-0 flex-col gap-1">
					<span className="text-2xs tracking-[0.02em] text-text-3">Production</span>
					<div className="flex items-center gap-1.5">
						<BareInput
							value={values.minDays}
							placeholder="min"
							onChange={(v) =>
								onChange({
									...values,
									minDays: v,
									touched: true,
									autoApplied: false,
								})
							}
						/>
						<span className="text-2xs text-text-3">–</span>
						<BareInput
							value={values.maxDays}
							placeholder="max"
							unit="days"
							onChange={(v) =>
								onChange({
									...values,
									maxDays: v,
									touched: true,
									autoApplied: false,
								})
							}
						/>
					</div>
				</div>
			</div>

			{/*
			 * The figures above are this tier converted, and saving an untouched
			 * price keeps it as typed. Changing the price is what re-authors it in
			 * this currency — worth saying before it happens. Null means unknown,
			 * so it says nothing.
			 */}
			{authoredCurrency && authoredCurrency !== currency && (
				<p className="px-2.5 pb-2 text-2xs text-text-4">
					Priced in {authoredCurrency}, shown in {currency}. Changing the price re-prices
					it in {currency} at today’s rate.
				</p>
			)}

			{values.quoted && (
				<p className="px-2.5 pb-2 text-2xs text-text-4">{quotedNote(values.quoted)}.</p>
			)}

			{extra && <div className="px-2.5 pb-2.5">{extra}</div>}
		</div>
	);
}

function PriceField({
	label,
	unit,
	value,
	placeholder,
	onChange,
}: {
	label: string;
	unit: string;
	value: string;
	placeholder?: string;
	onChange: (v: string) => void;
}) {
	return (
		<div className="flex min-w-0 flex-col gap-1">
			<span className="text-2xs tracking-[0.02em] text-text-3">{label}</span>
			<BareInput value={value} placeholder={placeholder} unit={unit} onChange={onChange} />
		</div>
	);
}

/** The design's input: a bordered box with the unit inside, not a separate chip. */
function BareInput({
	value,
	placeholder,
	unit,
	onChange,
}: {
	value: string;
	placeholder?: string;
	unit?: string;
	onChange: (v: string) => void;
}) {
	return (
		/* `fieldWrapper` rather than a hand-rolled box: it is the one recipe every
		   control in this app is built from, so these sit at the same 32px height
		   and wear the same focus ring as the inputs on every other form. */
		<div className={cn(fieldWrapper, 'min-w-0 flex-1 items-center pr-2')}>
			<input
				value={value}
				placeholder={placeholder ?? ''}
				onChange={(e) => onChange(e.target.value)}
				inputMode="decimal"
				className={cn(fieldWrapperInput, 'text-right font-mono', 'placeholder:text-text-4')}
			/>
			{unit && <span className="self-center text-2xs text-text-3">{unit}</span>}
		</div>
	);
}
